import { useEffect, useMemo, useState } from 'react';
import { api, type InvStatusRow } from '../lib/api';
import { DataTable, ErrorBox, dash, money, type Column, type Row } from '../components/DataTable';
import DriveMonPicker from '../components/DriveMonPicker';
import { pickDriveMon, useStickyDriveMon } from '../lib/driveMon';
import { hitAny, searchWords } from '../lib/search';

/**
 * 세금계산서 발행현황 — 개선요청 #54(최우석): 「해당 월의 세금계산서 발행 현황을 확인할 수 있는 메뉴 (정산이상유무 확인)」.
 * 서버 `functions/src/invoiceStatus.ts`. 읽기 전용 — 고치는 곳은 정산(청구액)·회계 발행대장(계산서)이다.
 * 한 줄 = 접수 하나: 정산한 청구액과 회계가 발행한 계산서를 나란히 두고 10원 이상 다르면 「차이」.
 * 범위는 정산 화면과 같다(담당 범위 안내는 껍데기의 `SettleScopeNote`).
 */

type State = InvStatusRow['state'];
const STATE: Record<State, { label: string; cls: string }> = {
  none: { label: '미발행', cls: 'bg-amber-50 text-amber-800' },
  requested: { label: '발행 요청 중', cls: 'bg-sky-50 text-sky-700' },
  pending: { label: '발행 준비', cls: 'bg-slate-100 text-slate-700' },
  issued: { label: '발행완료', cls: 'bg-brand-50 text-brand-700' },
  paid: { label: '입금완료', cls: 'bg-emerald-50 text-emerald-700' },
};
const FILTERS: { key: string; label: string; test: (r: InvStatusRow) => boolean }[] = [
  { key: 'all', label: '전체', test: () => true },
  ...(Object.keys(STATE) as State[]).map((s) => ({ key: s, label: STATE[s].label, test: (r: InvStatusRow) => r.state === s })),
  { key: 'diff', label: '금액 차이', test: (r) => r.diff !== null },
];

export default function InvoiceStatus() {
  const [months, setMonths] = useState<string[]>([]);
  const [mon, setMon] = useStickyDriveMon();
  const [rows, setRows] = useState<InvStatusRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    api.listDriveMonths()
      .then((r) => { setMonths(r.rows); setMon(pickDriveMon(r.rows, mon)); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    api.listInvoiceStatus({ driveMon: mon })
      .then((r) => { if (!cancelled) { setRows(r.rows); setTruncated(r.truncated); } })
      .catch((e) => { if (!cancelled) { setRows([]); setError(e instanceof Error ? e.message : String(e)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mon]);

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) ?? FILTERS[0]!;
    // #419 띄어쓰기 낱말 중 하나라도(OR) · 칸 안 띄어쓰기는 예전처럼 무시(이어 붙인 글자에서 찾는다).
    const words = searchWords(q);
    return rows.filter((r) => f.test(r)
      && hitAny(words, [`${r.recvId}${r.custName ?? ''}${r.owner ?? ''}${r.invNos ?? ''}`.replace(/\s/g, '')]));
  }, [rows, filter, q]);

  const columns: Column[] = [
    // phone: 폰에선 거래처를 왼쪽에 고정하고 번호·구분·담당·계산서 번호·작성일은 뺀다(모바일 7차).
    { key: 'recvId', label: '접수번호', phone: 'hide' },
    { key: 'custName', label: '거래처', phone: 'pin', render: (r) => <span className="font-medium">{dash(r.custName)}</span> },
    { key: 'recvKind', label: '구분', phone: 'hide', render: (r) => dash(r.recvKind) },
    { key: 'owner', label: '버스인접수담당자', phone: 'hide', render: (r) => dash(r.owner) },
    {
      key: 'billTotal', label: '청구액(정산)', align: 'right',
      render: (r) => (r.noBill ? <span className="text-xs text-red-700">정산 청구 없음</span> : money(r.billTotal)),
    },
    {
      key: 'state', label: '상태',
      render: (r) => {
        const s = STATE[r.state as State];
        return <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
      },
    },
    { key: 'invNos', label: '계산서 번호', phone: 'hide', render: (r) => <span className="text-xs">{dash(r.invNos)}</span> },
    { key: 'writtenDate', label: '작성일', phone: 'hide', render: (r) => dash(r.writtenDate) },
    { key: 'invTotal', label: '계산서 합계', align: 'right', render: (r) => money(r.invTotal) },
    {
      key: 'diff', label: '차이', align: 'right',
      render: (r) => (r.diff === null ? '' : <b className="text-red-700">{Number(r.diff) > 0 ? '+' : ''}{money(r.diff)}</b>),
    },
    { key: 'paidDate', label: '입금일', render: (r) => dash(r.paidDate) },
  ];

  const sum = (k: 'billTotal' | 'invTotal') => shown.reduce((s, r) => s + Number(r[k] ?? 0), 0);

  return (
    <div className="space-y-3">
      <div className="page-head">
        <h1 className="font-bold">세금계산서 발행현황</h1>
        <p className="text-sm text-ink-soft">
          정산월마다 정산한 청구액과 회계팀이 발행한 세금계산서를 나란히 봅니다. 금액이 다르면 「차이」에 빨갛게 표시됩니다.
        </p>
      </div>

      <div className="card flex flex-wrap items-center gap-2 p-3">
        <DriveMonPicker months={months} value={mon} onChange={setMon} />
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => {
            const n = rows.filter(f.test).length;
            if (f.key !== 'all' && !n) return null;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs ${filter === f.key ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-line text-ink-soft hover:bg-canvas'}`}>
                {f.label} <span className="tabular-nums">{n}</span>
              </button>
            );
          })}
        </div>
        <input className="field ml-auto w-56" placeholder="거래처·접수번호·계산서 번호·버스인접수담당자" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {error && <ErrorBox message={error} />}
      {truncated && <p className="text-xs text-amber-700">줄이 많아 앞쪽 3,000건만 보입니다. 검색으로 좁혀 주세요.</p>}

      <DataTable
        columns={columns}
        rows={shown as unknown as Row[]}
        loading={loading}
        tableId="invoice.status"
        rowClass={(r) => (r.diff !== null || r.noBill ? 'bg-red-50/40' : '')}
        total={{ count: shown.length, label: '합계', values: { billTotal: sum('billTotal'), invTotal: sum('invTotal') } }}
      />
      {!loading && mon && rows.length === 0 && !error && (
        <p className="text-sm text-ink-faint">{mon} 에는 세금계산서로 청구하는 정산이 없습니다.</p>
      )}
    </div>
  );
}
